import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Clock, User, CheckCircle, XCircle, AlertCircle, Target, Building2, ExternalLink, Loader2, Ban } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  cancelled: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  expired: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const CLAIM_STATUS_COLORS = {
  submitted: 'bg-yellow-500/20 text-yellow-400',
  pending: 'bg-purple-500/20 text-purple-400',
  approved: 'bg-blue-500/20 text-blue-400',
  completed: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
  forfeited: 'bg-zinc-500/20 text-zinc-400'
};

export default function BountyDetailPage() {
  const { bountyId } = useParams();
  const navigate = useNavigate();
  const [bounty, setBounty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchBounty();
    checkAuth();
  }, [bountyId]);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Fetch user's businesses
        const bizRes = await fetch(`${API}/businesses/user/list`, {
          credentials: 'include'
        });
        if (bizRes.ok) {
          const businesses = await bizRes.json();
          setUserBusinesses(businesses);
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
    }
  };

  const fetchBounty = async () => {
    try {
      const response = await fetch(`${API}/bounties/${bountyId}`);
      if (response.ok) {
        const data = await response.json();
        setBounty(data);
      } else {
        toast.error('Bounty not found');
        navigate('/bounties');
      }
    } catch (error) {
      console.error('Error fetching bounty:', error);
      toast.error('Failed to load bounty');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!selectedBusiness) {
      toast.error('Please select a business');
      return;
    }

    setClaiming(true);
    try {
      const response = await fetch(`${API}/bounties/${bountyId}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ business_id: selectedBusiness })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to claim bounty');
      }

      toast.success(data.message);
      setClaimDialogOpen(false);
      setSelectedBusiness('');
      fetchBounty();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setClaiming(false);
    }
  };

  const handleApproveClaim = async (claimId) => {
    setProcessing(claimId);
    try {
      const response = await fetch(`${API}/bounties/${bountyId}/approve/${claimId}`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to approve claim');
      }

      toast.success('Claim approved! MEL has been transferred.');
      fetchBounty();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectClaim = async (claimId) => {
    setProcessing(claimId);
    try {
      const response = await fetch(`${API}/bounties/${bountyId}/reject/${claimId}`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reject claim');
      }

      toast.success('Claim rejected');
      fetchBounty();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleCancelBounty = async () => {
    if (!confirm('Are you sure you want to cancel this bounty? Your MEL will be refunded.')) {
      return;
    }

    try {
      const response = await fetch(`${API}/bounties/${bountyId}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to cancel bounty');
      }

      toast.success('Bounty cancelled. MEL refunded to your wallet.');
      navigate('/bounties');
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-24 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="h-8 w-32 skeleton rounded mb-8" />
            <div className="h-64 skeleton rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!bounty) return null;

  const isCreator = user?.user_id === bounty.creator_id;
  const hasUserClaimed = bounty.claims?.some(c => c.claimer_id === user?.user_id && ['pending', 'submitted'].includes(c.status));
  const canClaim = user && !isCreator && bounty.status === 'active' && !hasUserClaimed;
  const canCancel = isCreator && bounty.status === 'active';
  const activeClaims = bounty.claims?.filter(c => ['pending', 'submitted'].includes(c.status)) || [];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <Link
              to="/bounties"
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-mono text-sm">Back to Bounty Board</span>
            </Link>
          </motion.div>

          {/* Bounty Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-8 mb-8"
          >
            {/* Status & Category */}
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full border text-sm font-medium ${STATUS_COLORS[bounty.status]}`}>
                {bounty.status.charAt(0).toUpperCase() + bounty.status.slice(1)}
              </span>
              {bounty.category && (
                <span className="px-3 py-1 rounded-full bg-zinc-800 text-zinc-400 text-sm">
                  {bounty.category}
                </span>
              )}
              {bounty.requires_approval && (
                <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-sm">
                  Requires Approval
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {bounty.title}
            </h1>

            {/* Location */}
            {(bounty.city || bounty.state) && (
              <div className="flex items-center gap-2 text-zinc-400 mb-4">
                <MapPin className="w-4 h-4" />
                <span>{[bounty.city, bounty.state].filter(Boolean).join(', ')}</span>
              </div>
            )}

            {/* Description */}
            {bounty.description && (
              <p className="text-zinc-300 mb-6">{bounty.description}</p>
            )}

            {/* Reward */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 mb-6">
              <img src={MELANIN_ICON} alt="" className="w-10 h-10" />
              <div>
                <p className="text-zinc-400 text-sm">Reward</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-green-400">{bounty.reward_amount}</span>
                  <span className="text-green-400/70">MEL</span>
                </div>
              </div>
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                {bounty.creator_picture ? (
                  <img src={bounty.creator_picture} alt="" className="w-5 h-5 rounded-full" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span>by {bounty.creator_name}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Created {new Date(bounty.created_at).toLocaleDateString()}</span>
              </div>

              {bounty.expires_at && (
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-400">
                    Expires {new Date(bounty.expires_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-zinc-800">
              {canClaim && (
                <button
                  onClick={() => setClaimDialogOpen(true)}
                  className="btn-primary flex items-center gap-2"
                  data-testid="claim-bounty-btn"
                >
                  <Target className="w-4 h-4" />
                  Claim This Bounty
                </button>
              )}

              {hasUserClaimed && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/10 text-yellow-400">
                  <Clock className="w-4 h-4" />
                  You have an active claim
                </div>
              )}

              {canCancel && activeClaims.length === 0 && (
                <button
                  onClick={handleCancelBounty}
                  className="btn-secondary flex items-center gap-2 text-red-400 hover:text-red-300"
                  data-testid="cancel-bounty-btn"
                >
                  <Ban className="w-4 h-4" />
                  Cancel & Refund
                </button>
              )}

              {canCancel && activeClaims.length > 0 && (
                <div className="text-zinc-500 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Cannot cancel while claims are active
                </div>
              )}
            </div>
          </motion.div>

          {/* Claims Section */}
          {bounty.claims && bounty.claims.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card rounded-xl p-6"
            >
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Claims ({bounty.claims.length})
              </h2>

              <div className="space-y-4">
                {bounty.claims.map((claim) => (
                  <div
                    key={claim.claim_id}
                    className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {claim.claimer?.picture ? (
                          <img src={claim.claimer.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                            <User className="w-5 h-5 text-zinc-500" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-white">{claim.claimer?.name || 'Unknown User'}</p>
                          {claim.business && (
                            <Link
                              to={`/businesses/${claim.business_id}`}
                              className="flex items-center gap-2 text-zinc-400 text-sm hover:text-white transition-colors mt-1"
                            >
                              <Building2 className="w-3 h-3" />
                              {claim.business.name}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${CLAIM_STATUS_COLORS[claim.status]}`}>
                              {claim.status}
                            </span>
                            {claim.business?.verification_status && (
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                claim.business.verification_status === 'verified'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                Business: {claim.business.verification_status}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Creator Actions */}
                      {isCreator && claim.status === 'pending' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApproveClaim(claim.claim_id)}
                            disabled={processing === claim.claim_id}
                            className="px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors text-sm flex items-center gap-1"
                          >
                            {processing === claim.claim_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectClaim(claim.claim_id)}
                            disabled={processing === claim.claim_id}
                            className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors text-sm flex items-center gap-1"
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </button>
                        </div>
                      )}

                      {/* Completed Badge */}
                      {claim.status === 'completed' && (
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">Winner</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>

      {/* Claim Dialog */}
      <Dialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Claim This Bounty</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Select one of your businesses to submit for this bounty. 
              {bounty.requires_approval 
                ? ' The bounty creator will review and approve your claim.'
                : ' If your business is verified, you\'ll automatically receive the reward.'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {userBusinesses.length > 0 ? (
              <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white" data-testid="select-business-for-claim">
                  <SelectValue placeholder="Select a business" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-zinc-800">
                  {userBusinesses.map(biz => (
                    <SelectItem 
                      key={biz.business_id} 
                      value={biz.business_id}
                      className="text-white hover:bg-zinc-800"
                    >
                      <div className="flex items-center gap-2">
                        <span>{biz.name}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          biz.verification_status === 'verified'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {biz.verification_status}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-center py-6">
                <Building2 className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400 mb-4">You haven&apos;t submitted any businesses yet.</p>
                <Link
                  to="/submit-business"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Submit a Business
                </Link>
              </div>
            )}
          </div>

          {userBusinesses.length > 0 && (
            <DialogFooter>
              <button
                onClick={() => setClaimDialogOpen(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleClaim}
                disabled={!selectedBusiness || claiming}
                className="btn-primary flex items-center gap-2"
                data-testid="confirm-claim-btn"
              >
                {claiming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Target className="w-4 h-4" />
                )}
                Submit Claim
              </button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
